import { useEffect, useState } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import type {
  ProfessionalRow,
  ProfessionalUpdate,
  ProfileProviderProps,
  UseProfileReturn,
} from '@/types'
import { profileContext } from './profile-context'

export const ProfileProvider = ({
  children,
}: ProfileProviderProps): JSX.Element => {
  const { user } = useAuth()
  const [profile, setProfile] = useState<ProfessionalRow | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    const fetchProfile = async (): Promise<undefined> => {
      setIsLoading(true)
      try {
        const { data, error } = await supabase
          .from('professionals')
          .select('*')
          .eq('user_id', user.id)
          .single()

        if (error) throw error
        setProfile(data)
      } catch (error) {
        console.error(
          error instanceof Error ? error.message : 'Error fetching profile',
        )
        toast.error('Failed to load profile')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchProfile()
  }, [user])

  const updateProfile = async (
    updates: ProfessionalUpdate,
  ): Promise<{
    data: ProfessionalRow | undefined
    error: Error | undefined
  }> => {
    if (!user) {
      toast.error('User not authenticated')

      return { data: undefined, error: new Error('User not authenticated') }
    }

    try {
      const { data, error } = await supabase
        .from('professionals')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error
      setProfile(data)
      toast.success('Profile updated successfully')

      return { data, error: undefined }
    } catch (error: unknown) {
      console.error(
        error instanceof Error ? error.message : 'Error updating profile',
      )
      toast.error('Failed to update profile. Please try again.')

      return {
        data: undefined,
        error: error instanceof Error ? error : new Error('Unknown error'),
      }
    }
  }

  const value: UseProfileReturn = { isLoading, profile, updateProfile }

  return (
    <profileContext.Provider value={value}>{children}</profileContext.Provider>
  )
}
